export type DateRangePreset = '7d' | '30d' | '90d' | '12mo';

export interface KpiMetric {
  label: string;
  value: number;
  previous_value: number | null;
  format: 'number' | 'percent' | 'duration' | 'string';
}

export interface AnalyticsOverview {
  active_users: KpiMetric;
  sessions: KpiMetric;
  page_views: KpiMetric;
  bounce_rate: KpiMetric;
  avg_session_duration: KpiMetric;
  pages_per_session: KpiMetric;
  new_users: KpiMetric;
  returning_users: KpiMetric;
  top_source: KpiMetric | null;
}

export interface TrafficTimeseriesPoint {
  date: string;
  active_users: number;
  sessions: number;
  page_views: number;
}

export interface TrafficTimeseries {
  data: TrafficTimeseriesPoint[];
}

export interface TrafficSourceEntry {
  source: string;
  sessions: number;
  percentage: number;
}

export interface TrafficSources {
  data: TrafficSourceEntry[];
}

export interface TopPageEntry {
  page_path: string;
  page_title: string;
  page_views: number;
  avg_engagement_time: number;
}

export interface TopPages {
  data: TopPageEntry[];
}

export interface GeographyEntry {
  country: string;
  sessions: number;
  users: number;
}

export interface GeographyData {
  data: GeographyEntry[];
}

export interface DeviceEntry {
  device_category: string;
  sessions: number;
  percentage: number;
}

export interface DeviceBreakdown {
  data: DeviceEntry[];
}

export interface UserTypes {
  new_users: number;
  returning_users: number;
}

export interface AnalyticsAccessResponse {
  has_access: boolean;
}

// --- Insights, Action Log, Digests ---

export interface AnalyticsIssue {
  title: string;
  severity: 'critical' | 'warning' | 'info';
  description: string;
  suggestion: string;
  insight_key?: string | null;
  goal_ref?: string | null;
  metric_name?: string | null;
  primary_framing?: string | null;
  goal_name?: string | null;
}

export interface AnalyticsRecommendation {
  text: string;
  insight_key?: string | null;
  goal_ref?: string | null;
  metric_name?: string | null;
  primary_framing?: string | null;
  goal_name?: string | null;
}

export interface GoalProgressEntry {
  goal_id?: string | null;
  goal_name: string;
  goal_type: 'numeric' | 'qualitative';
  /**
   * GA4 metric the values below are expressed in — it is what says 0.4 means a
   * 40% bounce rate rather than a count (#1267). Absent on qualitative goals
   * and on rows persisted before #1267.
   */
  metric_name?: string | null;
  current_value?: number | null;
  target_value?: number | null;
  status: 'active' | 'achieved' | 'abandoned';
  evidence?: string | null;
}

export interface AnalyticsInsights {
  id: string;
  week_start: string;
  issues: AnalyticsIssue[];
  recommendations: AnalyticsRecommendation[];
  goal_progress?: GoalProgressEntry[];
  generated_at: string;
}

export interface ActionLogEntry {
  id: string;
  action_date: string;
  description: string;
  entry_type: 'change' | 'note';
  created_by: string;
  created_at: string;
}

export interface ActionLogCreate {
  action_date: string;
  description: string;
  entry_type: 'change' | 'note';
}

export interface AnalyticsDigestEntry {
  id: string;
  week_start: string;
  subject: string;
  summary: string | null;
  html_content: string | null;
  sent_at: string | null;
  created_at: string;
}

// --- Strategy Layer: Goals, Strategy Doc, Insight Feedback ---

export type GoalType = 'numeric' | 'qualitative';
export type GoalStatus = 'active' | 'achieved' | 'abandoned';
export type FeedbackType = 'accepted' | 'rejected' | 'addressed';

export interface Goal {
  id: string;
  goal_type: GoalType;
  name: string;
  description: string | null;
  metric_name: string | null;
  target_value: number | null;
  target_date: string | null;
  status: GoalStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface GoalCreate {
  goal_type: GoalType;
  name: string;
  description?: string | null;
  metric_name?: string | null;
  target_value?: number | null;
  target_date?: string | null;
  status?: GoalStatus;
}

export interface GoalUpdate {
  name?: string;
  description?: string | null;
  metric_name?: string | null;
  target_value?: number | null;
  target_date?: string | null;
  status?: GoalStatus;
}

export interface StrategyContext {
  content: string;
  updated_by: string | null;
  updated_at: string;
}

export interface StrategyUpdate {
  content: string;
}

export interface StrategyHistoryEntry {
  id: string;
  content: string;
  updated_by: string | null;
  updated_at: string;
}

export interface InsightFeedback {
  id: string;
  week_start: string;
  insight_key: string;
  feedback_type: FeedbackType;
  rationale: string | null;
  created_by: string;
  created_at: string;
}

export interface InsightFeedbackCreate {
  week_start: string;
  insight_key: string;
  feedback_type: FeedbackType;
  rationale?: string | null;
}

// Hard caps mirroring Pydantic Field(max_length=...) on the backend.
// Used by the admin UI for client-side counters and submit-disable.
export const STRATEGY_CONTENT_MAX = 2000;
export const GOAL_NAME_MAX = 80;
export const GOAL_DESCRIPTION_MAX = 300;
export const FEEDBACK_RATIONALE_MAX = 500;

// Fixed list of GA4 metric identifiers supported by numeric goals. Mirrors
// the keys of _GOAL_METRIC_TO_OVERVIEW_FIELD in
// backend/services/analytics_insights_generator.py — keep in sync.
export const GOAL_METRIC_OPTIONS: { value: string; label: string; lowerIsBetter?: boolean }[] = [
  { value: 'activeUsers', label: 'Active users' },
  { value: 'sessions', label: 'Sessions' },
  { value: 'screenPageViews', label: 'Page views' },
  { value: 'bounceRate', label: 'Bounce rate', lowerIsBetter: true },
  { value: 'averageSessionDuration', label: 'Avg session duration' },
  { value: 'screenPageViewsPerSession', label: 'Pages per session' },
  { value: 'newUsers', label: 'New users' },
  { value: 'returningUsers', label: 'Returning users' },
];
